import React, { useEffect, useState } from "react";
import { apiGet } from "../services/api";
import PatientSearch, { type PatientResult } from "../components/doctor/PatientSearch";
import ClinicalBill from "../components/manager/ClinicalBill";
import AppShellLayout from "../components/layout/AppShellLayout";
import "../components/manager/ManagerBilling.css";

interface Patient {
  patient_id: string | number;
  name: string;
  gender?: string;
  age?: number;
}

interface Visit {
  visit_id: number;
  created_at?: string;
  doctor_name?: string;
  status?: string;
}

export default function BillingClinical() {
  const [patient, setPatient] = useState<Patient | null>(null);
  const [recentVisits, setRecentVisits] = useState<Visit[]>([]);
  const [selectedVisit, setSelectedVisit] = useState<Visit | null>(null);

  useEffect(() => {
    if (!patient) {
      setRecentVisits([]);
      setSelectedVisit(null);
      return;
    }

    const patientId = encodeURIComponent(String(patient.patient_id));
    apiGet(`/api/patients/${patientId}/visits?limit=5`)
      .then((res: unknown) => {
        const list = Array.isArray(res) ? (res as Visit[]) : [];
        setRecentVisits(list);
        if (list.length > 0) setSelectedVisit(list[0]);
      })
      .catch(() => setRecentVisits([]));
  }, [patient]);

  const handleSelect = (p: PatientResult) => {
    setPatient({
      patient_id: p.patient_id,
      name: p.name,
      gender: p.gender,
      age: p.age,
    });
  };

  return (
    <div className="billing-clinical-page">
      <AppShellLayout
        header={(
          <header className="billing-header">
            <div className="header-brand">
              <span className="icon">💳</span>
              <h3>Clinical Billing</h3>
            </div>
            {patient && (
              <div className="header-actions">
                <button
                  className="btn-text"
                  type="button"
                  onClick={() => {
                    setPatient(null);
                    setSelectedVisit(null);
                  }}
                >
                  🔄 Switch Patient
                </button>
              </div>
            )}
          </header>
        )}
        left={(
          <aside className="billing-sidebar">
            <div className="billing-left-icon collapse-only" title="Billing">💳</div>
            <div className="billing-left-content collapse-hide">
              {patient ? (
                <>
                  <div className="patient-card-billing">
                    <div className="p-avatar">{patient.name[0]}</div>
                    <div className="p-details">
                      <strong>{patient.name}</strong>
                      <span>{patient.patient_id}</span>
                      <small>{patient.gender} / {patient.age} yrs</small>
                    </div>
                  </div>

                  <div className="visit-selector">
                    <label>Link to Visit (Optional)</label>
                    <select
                      value={selectedVisit?.visit_id ?? ""}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                        const v = recentVisits.find(rv => rv.visit_id === Number(e.target.value));
                        setSelectedVisit(v || null);
                      }}
                    >
                      <option value="">-- Counter Bill (No Visit) --</option>
                      {recentVisits.map((v) => (
                        <option key={v.visit_id} value={v.visit_id}>
                          {new Date(v.created_at ?? "").toLocaleDateString()} - {v.doctor_name || "Doctor"}
                          {String(v.status || "").toUpperCase().replace(/\s+/g, "_").includes("LOCK") ? " (Locked)" : ""}
                        </option>
                      ))}
                    </select>
                    <small className="hint">
                      Selecting a visit loads the Doctor&apos;s procedures automatically.
                    </small>
                  </div>
                </>
              ) : (
                <div className="billing-sidebar-empty">
                  <h4>Billing Sidebar</h4>
                  <p>Pick a patient to view visit options.</p>
                </div>
              )}
            </div>
          </aside>
        )}
        main={(
          <>
            {!patient ? (
              <div className="search-overlay">
                <div className="search-box-centered">
                  <h2>Find Patient</h2>
                  <p>Search by name, mobile, or ID to start billing.</p>
                  <PatientSearch onSelect={handleSelect} />
                </div>
              </div>
            ) : (
              <div className="billing-workspace">
                <div className="workspace-panel fade-in">
                  <div className="panel-header">
                    <h4>Clinical Bill Generation</h4>
                    {selectedVisit && <span className="tag">Linked: Visit #{selectedVisit.visit_id}</span>}
                  </div>
                  <ClinicalBill patient={patient} visit={selectedVisit} />
                </div>
              </div>
            )}
          </>
        )}
      />
    </div>
  );
}
